import { createContext, useState, useCallback, useEffect, useRef } from 'react'
import { api } from '../services/api'
import { useAuth } from './AuthContext'

export const RateLimitContext = createContext({})

export function RateLimitProvider({ children }) {
  const { user } = useAuth()
  const isAuthenticated = !!user

  const [rateLimit, setRateLimit] = useState({
    used: 0,
    remaining: 3,
    limit: 3,
    resetAt: null,
    isLoading: true,
    error: null,
  })

  const [showUpgradeModal, setShowUpgradeModal] = useState(false)

  const hasFetchedRef = useRef(false)

  const fetchRateLimitStatus = useCallback(async () => {
    try {
      const response = await api.get('/usage')
      setRateLimit({
        used: response.data.used,
        remaining: response.data.remaining,
        limit: response.data.limit,
        resetAt: response.data.resetAt,
        isLoading: false,
        error: null,
      })
    } catch (error) {
      console.error('Error fetching rate limit status:', error)
      setRateLimit((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }))
    }
  }, [])

  const updateFromHeaders = useCallback((headers) => {
    if (headers['x-ratelimit-limit']) {
      setRateLimit({
        limit: parseInt(headers['x-ratelimit-limit'], 10),
        remaining: parseInt(headers['x-ratelimit-remaining'], 10),
        used: parseInt(headers['x-ratelimit-limit'], 10) - parseInt(headers['x-ratelimit-remaining'], 10),
        resetAt: headers['x-ratelimit-reset'],
        isLoading: false,
        error: null,
      })
    }
  }, [])

  const handleRateLimitError = useCallback((error) => {
    if (error.response?.status === 429) {
      const resetAt = error.response.data?.resetAt
      setRateLimit((prev) => ({
        ...prev,
        remaining: 0,
        used: prev.limit,
        resetAt,
        error: error.response.data?.message || 'Rate limit exceeded',
      }))
      setShowUpgradeModal(true)
      return true
    }
    return false
  }, [])

  const closeUpgradeModal = useCallback(() => {
    setShowUpgradeModal(false)
  }, [])

  const getTimeUntilReset = useCallback(() => {
    if (!rateLimit.resetAt) return null

    const resetTime = new Date(rateLimit.resetAt)
    const now = new Date()
    const diff = resetTime - now

    if (diff <= 0) return 'em breve'

    const hours = Math.floor(diff / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

    if (hours > 0) {
      return `${hours}h ${minutes}m`
    }
    return `${minutes}m`
  }, [rateLimit.resetAt])

  const isActiveSubscription = user?.subscriptionStatus === 'active'
  const canCalculate = isActiveSubscription || rateLimit.remaining === 'unlimited' || rateLimit.remaining > 0

  const getWarningMessage = useCallback(() => {
    if (rateLimit.remaining === 0) {
      return `Você atingiu o limite de ${rateLimit.limit} cálculos por dia. Tente novamente em ${getTimeUntilReset()}.`
    }
    if (rateLimit.remaining === 1) {
      return `Atenção: você tem apenas 1 cálculo restante hoje.`
    }
    return null
  }, [rateLimit.remaining, rateLimit.limit, getTimeUntilReset])

  useEffect(() => {
    if (!isAuthenticated) {
      setRateLimit({
        used: 0,
        remaining: 0,
        limit: 0,
        resetAt: null,
        isLoading: false,
        error: null,
      })
      hasFetchedRef.current = false
      return
    }

    if (hasFetchedRef.current) return

    hasFetchedRef.current = true
    setRateLimit((prev) => ({ ...prev, isLoading: true }))
    fetchRateLimitStatus()
  }, [fetchRateLimitStatus, isAuthenticated])

  const value = {
    rateLimit,
    canCalculate,
    fetchRateLimitStatus,
    updateFromHeaders,
    handleRateLimitError,
    getTimeUntilReset,
    getWarningMessage,
    showUpgradeModal,
    closeUpgradeModal,
  }

  return <RateLimitContext.Provider value={value}>{children}</RateLimitContext.Provider>
}
